import React from 'react';
import { WyscoutPlayerFixture } from '@/services/api';

interface WyscoutMatchTableProps {
  fixtures: WyscoutPlayerFixture[];
  selectedMatchId?: number | null;
  onOpenFixture?: (matchId: number, trigger: HTMLButtonElement) => void;
}

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

const metric = (fixture: WyscoutPlayerFixture, value: number | null, decimals = 0) => {
  if (fixture.participationStatus === 'did_not_play') return 'N/A';
  if (value === null) return '—';
  return decimals ? value.toFixed(decimals) : String(value);
};

const WyscoutMatchTable: React.FC<WyscoutMatchTableProps> = ({ fixtures, selectedMatchId, onOpenFixture }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" aria-label="Match-by-match history">
      <thead className="bg-gray-50 dark:bg-gray-800">
        <tr>
          {['Date', 'Fixture', 'Status', 'Pos', 'Min', 'G', 'A', 'xG', 'Shots', 'Pass%', 'Duels', 'Won', 'Int', 'Cards'].map((heading, index) => (
            <th key={heading} scope="col" className={`px-3 py-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400 ${index < 3 ? 'text-left' : 'text-center'}`}>{heading}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
        {fixtures.map(fixture => {
          const didNotPlay = fixture.participationStatus === 'did_not_play';
          const unavailable = !didNotPlay && fixture.statsStatus !== 'available';
          const selected = fixture.matchId === selectedMatchId;
          const matchLabel = fixture.label || [fixture.playerTeamName, fixture.opponentTeamName].filter(Boolean).join(' v ') || `Match ${fixture.matchId}`;
          const passAccuracy = fixture.passes !== null && fixture.successfulPasses !== null
            ? fixture.passes > 0 ? `${((fixture.successfulPasses / fixture.passes) * 100).toFixed(0)}%` : '0%'
            : null;
          const rowClass = selected
            ? 'bg-blue-50 text-gray-900 dark:bg-blue-950/40 dark:text-gray-100'
            : didNotPlay
            ? 'bg-gray-50 text-gray-600 dark:bg-gray-800/70 dark:text-gray-300'
            : unavailable
              ? 'bg-amber-50 text-gray-900 dark:bg-amber-950/20 dark:text-gray-100'
              : 'text-gray-900 hover:bg-green-50/40 dark:text-gray-100 dark:hover:bg-green-950/20';

          return (
            <tr key={fixture.matchId} className={rowClass} aria-current={selected || undefined}>
              <td className="whitespace-nowrap px-3 py-3 text-sm">{formatDate(fixture.dateUtc)}</td>
              <td className="min-w-48 px-3 py-3 text-sm" title={matchLabel}>
                <button type="button" data-wyscout-match-id={fixture.matchId} onClick={event => onOpenFixture?.(fixture.matchId, event.currentTarget)} className="font-medium text-left text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-400">{matchLabel}</button>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{fixture.competitionName || 'Competition unavailable'}{fixture.seasonName ? ` · ${fixture.seasonName}` : ''}</div>
              </td>
              <td className="min-w-44 px-3 py-3 text-sm">
                {didNotPlay ? (
                  <span className="inline-flex rounded-full bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">Did not play</span>
                ) : unavailable ? (
                  <>
                    <span className="inline-flex rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">Stats unavailable</span>
                    <p className="mt-1 max-w-60 text-xs text-amber-800 dark:text-amber-200">{fixture.failureMessage || 'Detailed statistics are unavailable for this fixture.'}</p>
                  </>
                ) : (
                  <span className="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800 dark:bg-green-900/50 dark:text-green-200">Played</span>
                )}
              </td>
              <td className="px-3 py-3 text-center text-sm">{didNotPlay ? 'N/A' : fixture.position ?? '—'}</td>
              <td data-testid={`fixture-minutes-${fixture.matchId}`} className="px-3 py-3 text-center text-sm">{metric(fixture, fixture.minutes)}</td>
              <td className="px-3 py-3 text-center text-sm">{metric(fixture, fixture.goals)}</td>
              <td className="px-3 py-3 text-center text-sm">{metric(fixture, fixture.assists)}</td>
              <td className="px-3 py-3 text-center text-sm">{metric(fixture, fixture.xg, 2)}</td>
              <td className="px-3 py-3 text-center text-sm">{metric(fixture, fixture.shots)}</td>
              <td className="px-3 py-3 text-center text-sm">{didNotPlay ? 'N/A' : passAccuracy ?? '—'}</td>
              <td className="px-3 py-3 text-center text-sm">{metric(fixture, fixture.duels)}</td>
              <td className="px-3 py-3 text-center text-sm">{metric(fixture, fixture.duelsWon)}</td>
              <td className="px-3 py-3 text-center text-sm">{metric(fixture, fixture.interceptions)}</td>
              <td className="px-3 py-3 text-center text-sm">
                {didNotPlay ? 'N/A' : unavailable ? '—' : `${fixture.yellowCards || 0}Y ${fixture.redCards || 0}R`}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default WyscoutMatchTable;
